/**
 * PD-16789 — keeping the storefront's idea of the signed-in user's accounts in
 * step with the server.
 *
 * The storefront used to learn the account list and the active account only
 * at sign-in, reload and biometric sign-in. When staff removed an account in
 * the CRM, an open storefront kept showing it (and kept the header on it)
 * until the next reload. The server now forgets an account the user can no
 * longer reach; these helpers decide when the storefront asks again and what
 * to do with the answer. Pure, so the rules are unit-tested without React.
 */

/** At most one refresh a minute from window focus / tab visibility. */
export const SESSION_REFRESH_MIN_INTERVAL_MS = 60_000;

/**
 * A burst of 409s (several queries failing together) needs one refresh, not
 * one each; this keeps a server that keeps answering 409 from looping.
 */
export const SESSION_CONFLICT_REFRESH_MIN_INTERVAL_MS = 5_000;

/** Whether enough time has passed since the last refresh to ask again. */
export function isRefreshDue(
	lastRefreshAt: number | null,
	now: number,
	minIntervalMs: number,
): boolean {
	return lastRefreshAt == null || now - lastRefreshAt >= minIntervalMs;
}

/** The part of a session the storefront compares before and after a refresh. */
export interface PortalSessionAccountsModel {
	activeClientId: number | null;
	clientIds: Array<number>;
}

export interface PortalSessionChangeModel {
	activeClientChanged: boolean;
	clientListChanged: boolean;
}

/**
 * What changed between two snapshots. The list is compared as a set, so the
 * server returning the same accounts in another order is not a change.
 */
export function diffPortalSessionAccounts(
	previous: PortalSessionAccountsModel,
	next: PortalSessionAccountsModel,
): PortalSessionChangeModel {
	const previousIds = new Set(previous.clientIds);
	const nextIds = new Set(next.clientIds);
	const clientListChanged =
		previousIds.size !== nextIds.size ||
		[...nextIds].some((id) => !previousIds.has(id));

	return {
		activeClientChanged: previous.activeClientId !== next.activeClientId,
		clientListChanged,
	};
}

/**
 * The user must pick an account: several to choose from and none active.
 * (One account is selected by the server automatically.)
 */
export function needsClientSelectionFor(session: {
	clients: Array<unknown>;
	activeClientId: number | null;
}): boolean {
	return session.clients.length > 1 && session.activeClientId == null;
}

function statusOf(error: unknown): unknown {
	return typeof error === 'object' && error != null && 'status' in error
		? (error as { status: unknown }).status
		: null;
}

/**
 * Switching to an account was refused: the server answers 403 when the
 * account is no longer one the user can reach.
 */
export function isAccountUnavailableError(error: unknown): boolean {
	return statusOf(error) === 403;
}

/**
 * Only 401 means the sign-in itself has expired and is worth refreshing.
 *
 * A 403 is the server saying no to this user (an account they no longer
 * have, a feature the storefront has switched off, pricing hidden), and a
 * fresh token is refused the same way. The API client used to refresh and
 * retry on 403 as well, which doubled every refusal and, when switching to
 * a removed account, made the second refusal look like a sign-in problem.
 * An expired or revoked token is answered 401 by crm-api's auth guard.
 */
export function isExpiredSignInStatus(status: number): boolean {
	return status === 401;
}

/**
 * The 409 every portal endpoint answers when the request needs an active
 * account and the server has none — including right after it forgot one the
 * user lost access to.
 */
export function isNoActiveClientConflict(
	status: number,
	body: unknown,
): boolean {
	if (status !== 409 || typeof body !== 'object' || body == null) {
		return false;
	}
	const message = (body as { message?: unknown }).message;
	return (
		typeof message === 'string' &&
		message.startsWith('No active client selected')
	);
}

type SessionStaleListener = () => void;

const sessionStaleListeners = new Set<SessionStaleListener>();

/**
 * Listen for API responses that say the storefront's account state is stale.
 * Returns the unsubscribe function.
 */
export function onPortalSessionStale(
	listener: SessionStaleListener,
): () => void {
	sessionStaleListeners.add(listener);
	return (): void => {
		sessionStaleListeners.delete(listener);
	};
}

/** Called by the API client when such a response arrives. */
export function notifyPortalSessionStale(): void {
	for (const listener of sessionStaleListeners) {
		listener();
	}
}
